import { Link } from "@/components/Link";
import { Facebook, Instagram, MessageCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { getAppSettings } from "@/api/settingsService";

const SOCIALS = {
  facebook: "https://www.facebook.com/yetfarrag/",
  instagram: "https://www.instagram.com/yetfarrag/",
  whatsapp: "https://wa.me/+201555987590",
};

type FooterColumn = {
  heading: string;
  // channel: chat/social channel name for chat_channel_clicked tracking
  links: { label: string; to?: string; href?: string; external?: boolean; channel?: string }[];
};

const COLUMNS: FooterColumn[] = [
  {
    heading: "استكشف",
    links: [
      { label: "كل المتاجر", to: "/stores" },
      { label: "حسب الفئة", to: "/categories" },
      { label: "حسب المحافظة", href: "#" },
      // F5 (SEO): site-wide SSR'd interlink to the blog index (Phase 51 D-13; the old guides
      // were folded into the blog). The footer is on every page, so crawlers reach it everywhere.
      { label: "المدونة", to: "/blog" },
    ],
  },
  {
    heading: "تواصل",
    links: [
      { label: "واتساب", href: SOCIALS.whatsapp, external: true, channel: "whatsapp" },
      { label: "إنستجرام", href: SOCIALS.instagram, external: true, channel: "instagram" },
      { label: "فيسبوك", href: SOCIALS.facebook, external: true, channel: "facebook" },
      { label: "سياسة الخصوصية", to: "/privacy-policy" },
      { label: "شروط الاستخدام", to: "/terms-of-service" },
      { label: "حذف البيانات", to: "/data-deletion" },
    ],
  },
];

const socialLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "1px solid var(--ys-rule-2)",
  background: "var(--ys-paper)",
  color: "var(--ys-ink-2)",
  textDecoration: "none",
  transition: "background 150ms, color 150ms, border-color 150ms",
};

const colHeadingStyle: React.CSSProperties = {
  fontFamily: "var(--ys-font)",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ys-ink-3)",
  margin: "0 0 12px",
};

const linkStyle: React.CSSProperties = {
  fontFamily: "var(--ys-font)",
  fontSize: 13,
  color: "var(--ys-ink-2)",
  textDecoration: "none",
  display: "block",
  padding: "4px 0",
  transition: "color 150ms",
};

/* Wordmark: يتفرج + orange dot — shared by full and minimal variants */
const Wordmark = ({ style }: { style?: React.CSSProperties }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: 2,
      ...style,
    }}
  >
    <span
      style={{
        fontFamily: "var(--ys-font)",
        fontWeight: 600,
        fontSize: 16,
        color: "var(--ys-primary)",
        lineHeight: 1,
      }}
    >
      يتفرج
    </span>
    <span
      style={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "var(--ys-primary)",
        marginBottom: 3,
        flexShrink: 0,
      }}
    />
  </div>
);

/* Three social icon anchors — shared by full and minimal variants */
const SocialIcons = () => {
  const { trackNavigation } = usePostHogTracking();
  return (
  <>
    <a
      href={SOCIALS.instagram}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="إنستجرام يتفرج"
      style={socialLinkStyle}
      onClick={() => trackNavigation.chatChannelClicked({ channel: "instagram", surface: "footer" })}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-primary-hover)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-primary)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-rule-2)";
      }}
    >
      <Instagram size={18} />
    </a>
    <a
      href={SOCIALS.facebook}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="فيسبوك يتفرج"
      style={socialLinkStyle}
      onClick={() => trackNavigation.chatChannelClicked({ channel: "facebook", surface: "footer" })}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-primary-hover)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-primary)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-rule-2)";
      }}
    >
      <Facebook size={18} />
    </a>
    <a
      href={SOCIALS.whatsapp}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="واتساب يتفرج"
      style={socialLinkStyle}
      onClick={() => trackNavigation.chatChannelClicked({ channel: "whatsapp", surface: "footer" })}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-primary-hover)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-primary)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)";
        (e.currentTarget as HTMLElement).style.borderColor = "var(--ys-rule-2)";
      }}
    >
      <MessageCircle size={18} />
    </a>
  </>
  );
};

type FooterProps = {
  variant?: "full" | "minimal";
};

const Footer = ({ variant = "full" }: FooterProps) => {
  const { trackNavigation } = usePostHogTracking();

  /**
   * The blacklist link is gated on the public-visibility flag, so it is CLIENT-ONLY: the server
   * render has no query result and therefore never emits it. That is deliberate. Crawl discovery
   * for /blacklist comes from the sitemap and from the cross-links between the two blacklist
   * pages, not from here — and a footer link that 404s for every visitor while the flag is off
   * would be worse than no link at all.
   */
  const { data: appSettings } = useQuery({
    queryKey: ["app-settings"],
    queryFn: getAppSettings,
    staleTime: 5 * 60 * 1000,
  });
  const blacklistEnabled = appSettings?.publicBlacklist?.enabled === true;

  if (variant === "minimal") {
    return (
      <footer
        style={{
          marginTop: 48,
          paddingTop: 32,
          borderTop: "2px solid var(--ys-primary)",
          background: "var(--ys-bg)",
          direction: "rtl",
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "0 28px 24px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          {/* Brand: wordmark + slogan */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: 10,
            }}
          >
            <Wordmark />
            <p
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-2)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              دليلك للمتاجر المصرية الصغيرة
            </p>
            {/* F5 (SEO): the blog interlink (Phase 51 D-13) must exist on store pages, which
                use this minimal variant — one small link, keeping the variant lean. */}
            <Link
              to="/blog"
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-2)",
                textDecoration: "underline",
              }}
            >
              المدونة
            </Link>
          </div>

          {/* Social icons */}
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <SocialIcons />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      style={{
        marginTop: 80,
        paddingTop: 32,
        borderTop: "2px solid var(--ys-primary)",
        background: "var(--ys-bg)",
        direction: "rtl",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "0 28px",
        }}
      >
        {/* Columns grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 32,
          }}
          className="footer-grid"
        >
          {/* Brand column */}
          <div>
            <Wordmark style={{ marginBottom: 10 }} />
            <p
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 13,
                color: "var(--ys-ink-2)",
                margin: "0 0 6px",
                lineHeight: 1.6,
              }}
            >
              دليلك للمتاجر المصرية الصغيرة
            </p>
            <p
              style={{
                fontFamily: "var(--ys-font)",
                fontSize: 12,
                color: "var(--ys-ink-3)",
                margin: 0,
              }}
            >
              من القاهرة، ٢٠٢٦
            </p>
          </div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h5 style={colHeadingStyle}>{col.heading}</h5>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {col.heading === "استكشف" && blacklistEnabled && (
                  <li key="blacklist">
                    <Link
                      to="/blacklist"
                      style={linkStyle}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink)")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)")
                      }
                    >
                      بلاك ليست التقييمات الوهمية
                    </Link>
                  </li>
                )}
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        style={linkStyle}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink)")
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)")
                        }
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        style={linkStyle}
                        target={link.external ? "_blank" : undefined}
                        rel={link.external ? "noopener noreferrer" : undefined}
                        onClick={
                          link.channel
                            ? () =>
                                trackNavigation.chatChannelClicked({
                                  channel: link.channel!,
                                  surface: "footer",
                                })
                            : undefined
                        }
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink)")
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.color = "var(--ys-ink-2)")
                        }
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Social row */}
        <div
          className="footer-socials"
          style={{
            marginTop: 32,
            display: "flex",
            gap: 12,
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <SocialIcons />
        </div>

        {/* Bottom strip */}
        <div
          style={{
            marginTop: 20,
            padding: "16px 0",
            borderTop: "1px solid var(--ys-rule)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 10,
              color: "var(--ys-ink-3)",
              letterSpacing: "0.08em",
            }}
          >
            © YETFARRAG · 2026
          </span>
          <span
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 10,
              color: "var(--ys-ink-3)",
              letterSpacing: "0.08em",
            }}
          >
            صُنع في القاهرة
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
